interface PaginationOptions {
  paginationElement: HTMLElement;
  activePage: number;
  totalPages: number;
  itemsPerPage: number;
  itemsPerPageId: string;
}

export default class AxPagination {
  options: PaginationOptions;

  constructor(options: PaginationOptions){
    this.options = options;
    if(!options.activePage) {
      this.options.activePage = 1;
    }
    if(!options.itemsPerPage) {
      this.options.itemsPerPage = 25;
    }
  }

  get axPaginationOptions(): object {
    return this.options;
  }

  init() {
    const pagesList = document.createElement("ul");
    this.buildPagination(pagesList);
    this.setEventReceivers();
    this.refreshEventReceivers()
    return this;
  }

  public buildPagination(pagesList: HTMLElement) {
    const pagesArray = () => {
      let arrayOfPages:any[] = [];

      // No ellipsis
      // If 7 pages or less, display everything
      if(this.options.totalPages <= 7){
        for (let i = 1; i <= this.options.totalPages; i++){
          arrayOfPages.push(i);
        }
      }

      // 2 ellipses - current page is 5 or less from either end
      else if (this.options.activePage > 3 && (this.options.totalPages - this.options.activePage) > 3) {
        arrayOfPages = [1, "...", `${(this.options.activePage-1)}`, `${this.options.activePage}`, `${this.options.activePage+1}`, "...", `${this.options.totalPages}`]
      }

      else if (this.options.activePage <= 3) {
        for(let i=1; i<=5; i++){
          arrayOfPages.push(i);
        }
        arrayOfPages.push("...");
        arrayOfPages.push(this.options.totalPages);
      }

      else if((this.options.totalPages - this.options.activePage) <= 3){
        arrayOfPages = [1, '...'];
        for (let i=this.options.totalPages-4; i<=this.options.totalPages; i++){
          arrayOfPages.push(i);
        }
      }
      return arrayOfPages;
    }
    const arrayOfPages = pagesArray()

    const prevBtn = this.options.paginationElement.querySelector(".ax-pagination__previous") as HTMLElement;
    const nextBtn = this.options.paginationElement.querySelector(".ax-pagination__next") as HTMLElement;
    const inputBox = this.options.paginationElement.querySelector(".ax-input-wrapper");
    const selectBox = this.options.paginationElement.querySelector(".ax-select-wrapper");

    const controlLabel = ()=>{
      const label = document.createElement("label");
      label.htmlFor = this.options.itemsPerPageId;
      label.classList.add("ax-pagination__control__label");
      return label
    }

    pagesList.classList.add("ax-pagination__pages");
    
    for(let i=0; i<arrayOfPages.length; i++){
      const pageBtn = document.createElement("li");
      pageBtn.classList.add("ax-pagination__page-item");

      if(parseInt(arrayOfPages[i]) === this.options.activePage) {
        pageBtn.classList.add("active")
      }

      pageBtn.innerHTML = `<a class="ax-pagination__page-link">${arrayOfPages[i]}</a>`
      pagesList.appendChild(pageBtn);
    }

    if(this.options.activePage  === 1){
      prevBtn?.classList.add("disabled");
    } else {
      prevBtn?.classList.remove("disabled");
    }

    if(this.options.activePage === this.options.totalPages) {
      nextBtn.classList.add("disabled");
    } else {
      nextBtn?.classList.remove("disabled");
    }
    this.options.paginationElement.insertBefore(pagesList, nextBtn);

    if(inputBox){      
      if(this.options.paginationElement.querySelector("select")){
        this.options.paginationElement.querySelector("select")?.remove();
      }

      if(this.options.paginationElement.querySelector("input")){
        this.options.paginationElement.querySelector("input")?.remove();
      }

      if(this.options.paginationElement.querySelector(".ax-pagination__control__label")){
        this.options.paginationElement.querySelector(".ax-pagination__control__label")?.remove();
      }

      const itemsPerPageInput = document.createElement("input");
      itemsPerPageInput.id = this.options.itemsPerPageId;
      itemsPerPageInput.classList.add("ax-input", "ax-input-compact", "ax-input--text");
      inputBox.appendChild(itemsPerPageInput);
      inputBox.insertAdjacentElement("afterend", controlLabel());
    }

    if(selectBox){
      if(this.options.paginationElement.querySelector("select")){
        this.options.paginationElement.querySelector("select")?.remove();
      }

      if(this.options.paginationElement.querySelector("input")){
        this.options.paginationElement.querySelector("input")?.remove();
      }

      if(this.options.paginationElement.querySelector(".ax-pagination__control__label")){
        this.options.paginationElement.querySelector(".ax-pagination__control__label")?.remove();
      }

      const options = [25, 50, 75, 100]
      const itemsPerPageSelect = document.createElement("select");
      itemsPerPageSelect.id = this.options.itemsPerPageId;
      itemsPerPageSelect.classList.add("ax-select");
      options.forEach(option => {
        const optionElement = document.createElement("option");
        optionElement.innerText = option.toString();
        itemsPerPageSelect.appendChild(optionElement);
      })
      selectBox.appendChild(itemsPerPageSelect);
      selectBox.insertAdjacentElement("afterend", controlLabel());
    }

  }

  public setEventReceivers(){
    const prevBtn = this.options.paginationElement.querySelector(".ax-pagination__previous") as HTMLElement;
    const nextBtn = this.options.paginationElement.querySelector(".ax-pagination__next") as HTMLElement;

    if(prevBtn) {
      prevBtn.addEventListener('mouseup', e => {
        if(prevBtn.classList.contains("disabled")){return}
        this.options.activePage = this.options.activePage-1;
        this.updatePagination();
      })
    }

    if(nextBtn) {
      nextBtn.addEventListener('mouseup', e => {
        if(nextBtn.classList.contains("disabled")){return}
        this.options.activePage = this.options.activePage+1;
        this.updatePagination();
      })
    }
  }

  public refreshEventReceivers() {
    const pageBtns = this.options.paginationElement.querySelectorAll(".ax-pagination__page-item");
    const itemsPerPageInput = this.options.paginationElement.querySelector(".ax-input--text");
    const itemsPerPageSelect = this.options.paginationElement.querySelector(".ax-select");

    pageBtns.forEach(pageBtn=>{
      pageBtn.addEventListener('mouseup', e => {
        const btn = e.target as HTMLElement;
        if(btn.innerText === "...") { return }
        this.options.activePage = (parseInt(btn.innerText));
        this.updatePagination();
      })
    })

    if(itemsPerPageInput){
      itemsPerPageInput.addEventListener('keydown', e => {
        if(e.type === "keydown" && (e as KeyboardEvent).key === "Enter") {
          const value = (e.target as HTMLInputElement).value
          this.options.itemsPerPage = parseInt(value);
        }
      })
    }

    if(itemsPerPageSelect){
      itemsPerPageSelect.addEventListener("change", e => {
        const value = (e.target as HTMLSelectElement).value;
        this.options.itemsPerPage = parseInt(value);
      })
    }

  }

  public updatePagination(options?: PaginationOptions){
    if(options){this.options = options};

    let pagesList = (this.options.paginationElement.querySelector("ul")) as HTMLElement;
    while(pagesList.firstChild){
      pagesList.removeChild(pagesList.firstChild);
    }
    this.buildPagination(pagesList);
    this.refreshEventReceivers()

  }


}